import React, { useContext, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { Button, Col, Form, InputGroup, Modal, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { Context } from "../../../utils/context";
import ModalSave from "../../common/ModelSave";
import { CancelButton, SaveButton } from "../../common/Button";
import AnswerImage from "./AnswerImage";
import "../../Tabels/Tabels.css";

const toMessage = (value, fallback) => {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map((v) => toMessage(v, "")).join(", ");
  return value.message || value.original?.sqlMessage || fallback;
};

const AddQuestion = (props) => {
  const { postData } = useContext(Context);
  const [showModal, setShowModal] = useState({ code: 0, message: "" });
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: { question: "", answers: [{ answer: "", file: null }] },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "answers",
  });
  const answerValues = watch("answers");

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();
      formData.append("question", data.question.trim());
      formData.append(
        "answers",
        JSON.stringify(data.answers.map((item) => item.answer.trim()))
      );
      // Image of each answer goes under its position in the answers list
      data.answers.forEach((item, index) => {
        if (item.file?.[0]) {
          formData.append(`answers[${index}][image]`, item.file[0]);
        }
      });
      const response = await postData("/admin/masters/question", formData);

      setShowModal({
        code: response?.code || (response?.success ? 200 : 400),
        message: response?.success
          ? response.message
          : toMessage(
              response?.errors || response?.message,
              "Unable to add question"
            ),
      });

      if (response?.success) {
        setTimeout(() => {
          setShowModal({ code: 0, message: "" });
          props.handleClose();
        }, 1000);
      } else {
        setTimeout(() => setShowModal({ code: 0, message: "" }), 1500);
      }
    } catch (error) {
      console.error("Error adding question:", error);
      setShowModal({ code: 500, message: "Unable to add question" });
    }
  };

  return (
    <>
      <Modal
        {...props}
        onHide={props.handleClose}
        size="lg"
        aria-labelledby="question-add-title"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="question-add-title">Add Question</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)} role="form">
            <Row>
              <Col md={12}>
                <Form.Group className="main-form-section mt-3">
                  <Form.Label>Question</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="e.g. What is your skin type?"
                      className={classNames({
                        "is-invalid": errors.question,
                      })}
                      {...register("question", {
                        required: "Question is required",
                        validate: (value) =>
                          value.trim() !== "" || "Question is required",
                        maxLength: {
                          value: 250,
                          message: "Question must be 250 characters or less",
                        },
                      })}
                    />
                  </InputGroup>
                  {errors.question && (
                    <span className="text-danger">
                      {errors.question.message}
                    </span>
                  )}
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="main-form-section mt-3">
                  <Form.Label>Answers</Form.Label>
                  {fields.map((field, index) => (
                    <div key={field.id} className="mb-3 border rounded p-2">
                      <InputGroup>
                        <Form.Control
                          type="text"
                          placeholder={`Answer ${index + 1}`}
                          className={classNames({
                            "is-invalid": errors.answers?.[index]?.answer,
                          })}
                          {...register(`answers.${index}.answer`, {
                            required: "Answer is required",
                            validate: (value) =>
                              value.trim() !== "" || "Answer is required",
                            maxLength: {
                              value: 250,
                              message: "Answer must be 250 characters or less",
                            },
                          })}
                        />
                        <Button
                          variant="outline-danger"
                          onClick={() => remove(index)}
                          disabled={fields.length === 1}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </InputGroup>
                      {errors.answers?.[index]?.answer && (
                        <span className="text-danger">
                          {errors.answers[index].answer.message}
                        </span>
                      )}
                      <AnswerImage
                        inputProps={register(`answers.${index}.file`)}
                        file={answerValues?.[index]?.file?.[0]}
                      />
                    </div>
                  ))}
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => append({ answer: "", file: null })}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Add Answer
                  </Button>
                </Form.Group>
              </Col>
              <Row className="mt-5 pb-3">
                <div className="d-flex justify-content-center">
                  <CancelButton
                    name="cancel"
                    handleClose={props.handleClose}
                  />
                  <SaveButton
                    name="save"
                    handleSubmit={handleSubmit(onSubmit)}
                  />
                </div>
              </Row>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>
      <ModalSave
        message={showModal.message}
        showErrorModal={Boolean(showModal.code)}
      />
    </>
  );
};

export default AddQuestion;
